import { CbmImpl } from "~/modules/cbm";
import type { CbmChannel } from "~/modules/cbm";
import { Action } from "~/modules/actions/Action";
import { checkReservationExists, checkWorker, getReservationsByTask } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";





export interface TaskChannel {
    sid: string;
    interactionSid: string;
    type: string;
    status: string;
    mediaSid: string;
}





























export class GetChannelsForTask implements Action<Promise<TaskChannel[]>> {
    


    readonly #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}): Promise<TaskChannel[]> {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`GetChannelsForTask invoked with taskSid: ${this.#taskSid}`);

        const actionContext = {
            actionName: "GetChannelsForTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.GetChannelsForTaskCompleted,
            extra: { taskSid: this.#taskSid }
        };

        checkWorker(worker, actionContext);

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];
        checkReservationExists(reservation, this.#taskSid, actionContext);

        const task = reservation.task;

        try {
            const channels: CbmChannel[] = await cbm.getChannels(task);

            const result: TaskChannel[] = channels.map((channel) => ({
                sid: channel.sid,
                interactionSid: channel.interactionSid,
                type: channel.type,
                status: channel.status,
                mediaSid: channel.mediaSid
            }));

            sendWorkerTrackingEvent(EVENTS.GetChannelsForTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                channelCount: channels.length
            });

            return result;
        } catch (error) {
            const errorMsg = `GetChannelsForTask: Failed to get channels for task ${this.#taskSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.GetChannelsForTaskCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid
            });

            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToGetChannelsForTask,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
